import { createSignal, Show, For, createMemo, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import { gold, qty, medal, ymd, weekRangeLabel } from '../lib/format.js';
import { pickUser } from '../lib/picks.js';
import toast from '../lib/toastStore.js';
import MyRaffle from './MyRaffle.jsx';
import AdminRaffle from './AdminRaffle.jsx';
import './Raffle.css';

const GUILDS = [
  { id: 'post', label: 'Trading Post', full: 'Redfur Trading Post', emoji: '🛒', accent: 'v2-stat--cyan' },
  { id: 'dealers', label: 'Dealers', full: 'Redfur Dealers', emoji: '💼', accent: 'v2-stat--gold' },
];

function normalizeGuildSelection(raw) {
  const v = String(raw || 'all').toLowerCase();
  if (v === 'post' || v === 'dealers') return v;
  return 'all';
}

export default function Raffle(props) {
  const initialTab = () => {
    const t = props.widget?.type;
    if (t === 'my-raffle') return 'my';
    if (t === 'admin-raffle') return 'admin';
    return props.settings?.tab || 'main';
  };
  const [raffleTab, setRaffleTab] = createSignal(initialTab());
  const isAdmin = () => !!wm.state.identity?.isAdmin;
  // Read optional URL params on initial load
  const getUrlParams = () => {
    if (typeof window === 'undefined') return { week: null, guild: null, tag: null };
    try {
      const hash = window.location.hash || '';
      const queryIdx = hash.indexOf('?');
      const hashQuery = queryIdx >= 0 ? hash.slice(queryIdx + 1) : '';
      const p = new URLSearchParams(hashQuery || window.location.search || '');
      return {
        week: p.get('week') ? Number(p.get('week')) : null,
        guild: p.get('guild'),
        tag: p.get('tag') || p.get('user') || p.get('search'),
      };
    } catch {
      return { week: null, guild: null, tag: null };
    }
  };

  const initialUrl = getUrlParams();
  const [weekStart, setWeekStart] = createSignal(initialUrl.week || props.settings?.weekStart || null);
  const [selectedGuild, setSelectedGuild] = createSignal(normalizeGuildSelection(initialUrl.guild || props.settings?.guild));
  const [search, setSearch] = createSignal(initialUrl.tag || props.settings?.tag || '');
  const [debouncedSearch, setDebouncedSearch] = createSignal(initialUrl.tag || props.settings?.tag || '');
  const [showRules, setShowRules] = createSignal(!!props.settings?.showRules);
  const [showHistory, setShowHistory] = createSignal(!!props.settings?.showHistory);

  function persistRaffleSettings(patch) {
    if (!props.widget?.id) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), ...patch });
    } catch { /* best-effort */ }
  }

  let searchTimer = null;
  onCleanup(() => { if (searchTimer) clearTimeout(searchTimer); });

  function updateSearch(val, immediate = false) {
    setSearch(val);
    if (searchTimer) { clearTimeout(searchTimer); searchTimer = null; }
    if (immediate) {
      setDebouncedSearch(val);
      persistRaffleSettings({ tag: val.trim() || null });
      return;
    }
    searchTimer = setTimeout(() => {
      searchTimer = null;
      setDebouncedSearch(val);
      persistRaffleSettings({ tag: val.trim() || null });
    }, 220);
  }

  const limit = createMemo(() => {
    const n = Number(props.settings?.limit);
    if (!Number.isFinite(n)) return 150;
    return Math.min(250, Math.max(10, n));
  });

  // Query widget endpoint (including debounced tag search for full history)
  const { data, loading, refreshing, error } = useWidgetData('raffle', () => ({
    weekStart: weekStart(),
    limit: limit(),
    tag: debouncedSearch().trim() || undefined,
  }));

  const weeks = createMemo(() => data()?.weeks || []);
  const currentWeek = () => data()?.currentWeekStart ?? null;
  const latestDrawnWeek = () => data()?.latestDrawnWeekStart ?? null;
  const rowData = createMemo(() => data()?.leaderboards || { post: [], dealers: [] });
  const winnersData = createMemo(() => data()?.winners || { post: [], dealers: [] });
  const recentWinnersData = createMemo(() => data()?.recentWinners || { post: [], dealers: [] });
  const statusData = createMemo(() => data()?.status || { post: 'open', dealers: 'open' });
  const totalsData = createMemo(() => data()?.totals || {});
  const prizesData = createMemo(() => data()?.prizes || {});
  const sealedData = createMemo(() => data()?.isSealed || {});
  const userHistoryData = createMemo(() => data()?.userHistory || null);

  const activeWeek = createMemo(() => {
    const ts = data()?.weekStart;
    if (!ts) return null;
    return weeks().find(w => Number(w.week_start) === Number(ts)) || { week_start: ts };
  });

  const visibleGuilds = createMemo(() => {
    const g = selectedGuild();
    if (g === 'all') return GUILDS;
    return GUILDS.filter(x => x.id === g);
  });

  function copyVerificationLink() {
    const origin = (typeof window !== 'undefined' && window.location?.origin)
      ? window.location.origin
      : 'https://redfur.ech-o.net';
    const url = `${origin}/#raffle`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success('Copied raffle link to clipboard!', { detail: url }))
        .catch(() => toast.info('Raffle link: ' + url));
    } else {
      toast.info('Raffle link: ' + url);
    }
  }

  // Check if search tag matches any entrant in the selected week's active guilds
  const searchedActiveMatch = createMemo(() => {
    const q = search().trim().toLowerCase();
    if (!q) return null;
    const cleanQ = q.startsWith('@') ? q : '@' + q;

    const results = [];
    for (const g of GUILDS) {
      const rows = rowData()[g.id] || [];
      const match = rows.find(r => {
        const u = (r.user_tag || '').toLowerCase();
        return u === cleanQ || u === q || u.includes(q);
      });
      if (match) {
        const winners = winnersData()[g.id] || [];
        const win = winners.find(w => (w.user_tag || '').toLowerCase() === (match.user_tag || '').toLowerCase());
        const totals = totalsData()[g.id] || {};
        const totalTickets = Number(totals.tickets) || rows.reduce((s, r) => s + (Number(r.tickets) || 0), 0) || 1;
        const odds = ((Number(match.tickets) / totalTickets) * 100).toFixed(1);
        results.push({ guild: g, match, win, odds });
      }
    }
    return results.length ? results : null;
  });

  return (
    <div class="v2-widget v2-raffle">
      <div class="v2-tabs" role="tablist" aria-label="Raffle views" style={{ 'margin-bottom': '6px' }}>
        <button
          type="button"
          role="tab"
          aria-selected={raffleTab() === 'main'}
          class={`v2-tab ${raffleTab() === 'main' ? 'is-active' : ''}`}
          onClick={() => setRaffleTab('main')}
        >
          🎟️ Leaderboard & Winners
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={raffleTab() === 'my'}
          class={`v2-tab ${raffleTab() === 'my' ? 'is-active' : ''}`}
          onClick={() => setRaffleTab('my')}
        >
          👤 My Tickets
        </button>
        <Show when={isAdmin()}>
          <button
            type="button"
            role="tab"
            aria-selected={raffleTab() === 'admin'}
            class={`v2-tab ${raffleTab() === 'admin' ? 'is-active' : ''}`}
            onClick={() => setRaffleTab('admin')}
          >
            🛡️ Manage / Void
          </button>
        </Show>
      </div>
      <Show when={raffleTab() === 'my'}>
        <MyRaffle {...props} />
      </Show>
      <Show when={raffleTab() === 'admin' && isAdmin()}>
        <AdminRaffle {...props} />
      </Show>
      <Show when={raffleTab() === 'main'}>
      {/* Header bar */}
      <div class="v2-raffle__header">
        <div class="v2-raffle__title-wrap">
          <div class="v2-raffle__badge-row">
            <span class="v2-raffle__main-title">🎟️ Redfur Guild Raffle</span>
            <span class="v2-raffle__badge">PROVABLY TRANSPARENT</span>
            <Show when={refreshing()}>
              <span class="v2-raffle__refresh-pulse">◐ Updating…</span>
            </Show>
          </div>
          <div class="v2-raffle__subtitle">
            Verifiable ticket ranges & immutable in-game bank deposit ledgers
          </div>
        </div>

        <div class="v2-raffle__actions">
          <button
            class={`v2-btn-sm v2-raffle__action-btn ${showRules() ? 'v2-raffle__action-btn--active' : ''}`}
            onClick={() => { const next = !showRules(); setShowRules(next); persistRaffleSettings({ showRules: next }); }}
            title="How the raffle draw and ticket ranges work"
          >
            {showRules() ? '📖 Hide Rules' : '📖 How It Works'}
          </button>
          <button
            class="v2-btn-sm v2-raffle__action-btn"
            onClick={copyVerificationLink}
            title="Copy public verification link"
          >
            🔗 Share Link
          </button>
        </div>
      </div>

      {/* Explainer Drawer (Provably Honest & How It Works) */}
      <Show when={showRules()}>
        <div class="v2-raffle__explainer">
          <div class="v2-raffle__explainer-head">
            <span class="v2-raffle__explainer-title">🎲 Provably Honest Raffle System</span>
            <button class="v2-raffle__explainer-close" onClick={() => { setShowRules(false); persistRaffleSettings({ showRules: false }); }}>✕</button>
          </div>
          <div class="v2-raffle__explainer-grid">
            <div class="v2-raffle__rule-card">
              <div class="v2-raffle__rule-icon">🪙</div>
              <div class="v2-raffle__rule-title">1,000g = 1 Ticket</div>
              <div class="v2-raffle__rule-desc">
                Tickets are recorded automatically from in-game guild bank deposits. No manual entries or favoritism.
              </div>
            </div>

            <div class="v2-raffle__rule-card">
              <div class="v2-raffle__rule-icon">🔢</div>
              <div class="v2-raffle__rule-title">Verifiable Ticket Ranges</div>
              <div class="v2-raffle__rule-desc">
                Entrants hold contiguous ticket spans (e.g. <code>#501 – #650</code>). All numbers are public and auditable below.
              </div>
            </div>

            <div class="v2-raffle__rule-card">
              <div class="v2-raffle__rule-icon">🎯</div>
              <div class="v2-raffle__rule-title">Uniform Random Roll</div>
              <div class="v2-raffle__rule-desc">
                Random winning numbers are rolled across the entire ticket pool (<code>1 .. Total Tickets</code>). Whoever owns the winning number takes the place!
              </div>
            </div>

            <div class="v2-raffle__rule-card">
              <div class="v2-raffle__rule-icon">🛡️</div>
              <div class="v2-raffle__rule-title">Fair Distribution & Splits</div>
              <div class="v2-raffle__rule-desc">
                Post: 30% / 20% / 10% (40% keeps). Dealers: 25% / 15% / 10% (50% keeps). Post winners are excluded from Dealers to share the luck!
              </div>
            </div>
          </div>
          <div class="v2-raffle__explainer-footer">
            🔒 All finalized draws are permanently sealed in our SQLite Golden Ledger. No login is required to inspect tickets or verify outcomes.
          </div>
        </div>
      </Show>

      {/* Toolbar: Week Picker & Guild Filter Tabs */}
      <div class="v2-raffle__toolbar">
        <div class="v2-raffle__week-selector">
          <Show when={weeks().length}>
            <select
              class="v2-input v2-raffle__week-select"
              onChange={(e) => {
                const val = e.currentTarget.value;
                const nextWeek = val ? Number(val) : null;
                setWeekStart(nextWeek);
                persistRaffleSettings({ weekStart: nextWeek });
              }}
            >
              <For each={weeks()}>{(w) => {
                const ws = Number(w.week_start);
                const isCur = ws === Number(currentWeek());
                const isLatest = ws === Number(latestDrawnWeek());
                const isSelected = weekStart() == null ? isLatest : ws === Number(weekStart());

                let label = ymd(ws);
                if (isLatest) label = `🏆 Latest Draw (Winners): ${ymd(ws)}`;
                else if (isCur) label = `🎟️ Current Week (In Progress): ${ymd(ws)}`;
                else label = `📜 Draw Week: ${ymd(ws)}`;

                return (
                  <option value={w.week_start} prop:selected={isSelected}>
                    {label}
                  </option>
                );
              }}</For>
            </select>
          </Show>
        </div>

        {/* Guild Tabs (Trading Post & Dealers only) */}
        <div class="v2-raffle__guild-tabs" role="tablist">
          <button
            class={`v2-raffle__guild-tab ${selectedGuild() === 'all' ? 'v2-raffle__guild-tab--active' : ''}`}
            onClick={() => { setSelectedGuild('all'); persistRaffleSettings({ guild: 'all' }); }}
            role="tab"
            aria-selected={selectedGuild() === 'all'}
          >
            All Guilds
          </button>
          <For each={GUILDS}>{(g) => (
            <button
              class={`v2-raffle__guild-tab ${selectedGuild() === g.id ? 'v2-raffle__guild-tab--active' : ''}`}
              onClick={() => { setSelectedGuild(g.id); persistRaffleSettings({ guild: g.id }); }}
              role="tab"
              aria-selected={selectedGuild() === g.id}
            >
              {g.emoji} {g.label}
            </button>
          )}</For>
        </div>
      </div>

      {/* Search / Self-Lookup Input (Fixed padding & overlap) */}
      <div class="v2-raffle__search-bar">
        <div class="v2-raffle__search-box">
          <span class="v2-raffle__search-icon" aria-hidden="true">🔍</span>
          <input
            type="text"
            class="v2-input v2-raffle__search-input"
            placeholder="Search your @tag (e.g. @EchoTiger or taskibeowolf)..."
            value={search()}
            onInput={(e) => updateSearch(e.target.value, false)}
          />
          <Show when={search()}>
            <button class="v2-raffle__search-clear" onClick={() => updateSearch('', true)} title="Clear search">✕</button>
          </Show>
        </div>
      </div>

      {/* Player Spotlight & Historical Deposit Record Card */}
      <Show when={search().trim()}>
        <div class="v2-raffle__spotlight">
          <div class="v2-raffle__spotlight-head">
            <div class="v2-raffle__spotlight-title">
              <span>🎯 Player Record: <strong class="v2-raffle__spotlight-tag">{search()}</strong></span>
            </div>

            <div class="v2-raffle__history-toggle-wrap">
              <button
                class={`v2-btn-sm v2-raffle__history-btn ${showHistory() ? 'v2-raffle__history-btn--active' : ''}`}
                onClick={() => { const next = !showHistory(); setShowHistory(next); persistRaffleSettings({ showHistory: next }); }}
                title="Toggle lifetime deposits and past raffle history"
              >
                {showHistory() ? '📂 Show This Week Only' : '🕒 View All Historical Deposits & Wins'}
              </button>
            </div>
          </div>

          {/* Mode 1: Selected Week Match */}
          <Show when={!showHistory()}>
            <Show when={searchedActiveMatch()} fallback={
              <div class="v2-raffle__spotlight-empty">
                No tickets on record for <code class="v2-gold">{search()}</code> in the selected week ({activeWeek() ? ymd(activeWeek().week_start) : ''}).
                <Show when={userHistoryData()?.lifetime?.entries > 0}>
                  {' '}Click <strong>"View All Historical Deposits & Wins"</strong> to see their {userHistoryData().lifetime.entries} past deposits!
                </Show>
              </div>
            }>
              <div class="v2-raffle__spotlight-grid">
                <For each={searchedActiveMatch()}>{(item) => (
                  <div class={`v2-raffle__spotlight-item ${item.win ? 'v2-raffle__spotlight-item--win' : ''}`}>
                    <div class="v2-raffle__spotlight-guild">
                      {item.guild.emoji} <strong>{item.guild.label}</strong>
                      <Show when={item.win}>
                        <span class="v2-raffle__spotlight-winbadge">
                          🏆 {medal(item.win.place)} Winner ({gold(item.win.prize)})
                        </span>
                      </Show>
                    </div>
                    <div class="v2-raffle__spotlight-stats">
                      <div>
                        <span class="v2-raffle__spotlight-statlbl">Tickets:</span>
                        <strong>{qty(item.match.tickets)} 🎫</strong>
                      </div>
                      <div>
                        <span class="v2-raffle__spotlight-statlbl">Odds:</span>
                        <strong>{item.odds}%</strong>
                      </div>
                      <div>
                        <span class="v2-raffle__spotlight-statlbl">Ticket Range:</span>
                        <span class="v2-raffle__ticket-pill">
                          #{item.match.rangeStart ?? 1} – #{item.match.rangeEnd ?? item.match.tickets}
                        </span>
                      </div>
                      <div>
                        <span class="v2-raffle__spotlight-statlbl">Deposits:</span>
                        <strong>{qty(item.match.entries)} ({gold(item.match.gold)})</strong>
                      </div>
                    </div>
                    <Show when={item.win?.ticket}>
                      <div class="v2-raffle__spotlight-rolled">
                        🎲 Winning Ticket Rolled: <span class="v2-raffle__ticket-pill v2-raffle__ticket-pill--gold">#{item.win.ticket}</span> (Inside your range!)
                      </div>
                    </Show>
                  </div>
                )}</For>
              </div>
            </Show>
          </Show>

          {/* Mode 2: Full Lifetime & Historical Deposits View */}
          <Show when={showHistory()}>
            <Show when={userHistoryData() && userHistoryData().weekly?.length} fallback={
              <div class="v2-raffle__spotlight-empty">No historical raffle deposits found for {search()}.</div>
            }>
              <div class="v2-raffle__career">
                {/* Career High-level Stats */}
                <div class="v2-raffle__career-grid">
                  <div class="v2-stat v2-stat--gold">
                    <span class="v2-stat__label">Lifetime Deposited</span>
                    <span class="v2-stat__value">{gold(userHistoryData().lifetime.gold)}</span>
                    <span class="v2-stat__sub">{qty(userHistoryData().lifetime.entries)} total deposits</span>
                  </div>
                  <div class="v2-stat v2-stat--cyan">
                    <span class="v2-stat__label">Lifetime Tickets</span>
                    <span class="v2-stat__value">{qty(userHistoryData().lifetime.tickets)} 🎫</span>
                    <span class="v2-stat__sub">Across all draws</span>
                  </div>
                  <div class="v2-stat v2-stat--green">
                    <span class="v2-stat__label">Draw Wins</span>
                    <span class="v2-stat__value">{userHistoryData().lifetime.winCount} 🏆</span>
                    <span class="v2-stat__sub">Times won podium</span>
                  </div>
                  <div class="v2-stat v2-stat--gold">
                    <span class="v2-stat__label">Total Prize Won</span>
                    <span class="v2-stat__value">{gold(userHistoryData().lifetime.prizeGold)}</span>
                    <span class="v2-stat__sub">Gold paid out</span>
                  </div>
                </div>

                {/* Multi-week history table */}
                <div class="v2-raffle__history-ledger">
                  <div class="v2-raffle-ledger__title">📜 Week-by-Week Participation History</div>
                  <div class="v2-widget__scroll v2-raffle__history-scroll">
                    <table class="v2-raffle-table">
                      <thead>
                        <tr>
                          <th>Raffle Week</th>
                          <th>Guild</th>
                          <th>Tickets</th>
                          <th>Deposited</th>
                          <th>Outcome / Prize</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={userHistoryData().weekly}>{(w) => {
                          const win = (userHistoryData().wins || []).find(
                            x => Number(x.week_start) === Number(w.week_start) && x.guild === w.guild
                          );
                          const gLabel = w.guild === 'post' ? 'Trading Post' : 'Dealers';
                          const gEmoji = w.guild === 'post' ? '🛒' : '💼';
                          return (
                            <tr class={`v2-raffle-row ${win ? 'v2-raffle-row--winner' : ''}`}>
                              <td>
                                <span class="v2-raffle-history-link" onClick={() => {
                                  setWeekStart(Number(w.week_start));
                                  setSelectedGuild(w.guild);
                                  setShowHistory(false);
                                }}>
                                  {ymd(w.week_start)} ◱
                                </span>
                              </td>
                              <td>{gEmoji} {gLabel}</td>
                              <td>{qty(w.tickets)} 🎫</td>
                              <td>{gold(w.gold)}</td>
                              <td>
                                <Show when={win} fallback={<span class="v2-dim">Entered ({w.entries} dep)</span>}>
                                  <span class="v2-raffle-inline-win">
                                    {medal(win.place)} {win.place === 1 ? '1st' : win.place === 2 ? '2nd' : '3rd'} Place (Ticket #{win.ticket}) +{gold(win.prize)}
                                  </span>
                                </Show>
                              </td>
                            </tr>
                          );
                        }}</For>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Show>
          </Show>
        </div>
      </Show>

      {/* Main Content Area (With refreshing dimming & state indicators) */}
      <Show when={!loading()} fallback={<div class="v2-widget-skel">◐ Loading Raffle Ledgers…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class={`v2-raffle-content ${refreshing() ? 'v2-raffle-content--refreshing' : ''}`}>
            <Show when={activeWeek()}>
              <div class="v2-raffle-week">
                <div class="v2-raffle-week__left">
                  <span class="v2-raffle-week__label">Active Raffle Week</span>
                  <span class="v2-raffle-week__value">{weekRangeLabel(activeWeek().week_start)}</span>
                </div>
                <div class="v2-raffle-week__right">
                  <span class="v2-raffle-week__sub">1,000g = 1 ticket · Verified in-game deposit records</span>
                </div>
              </div>
            </Show>

            <div class="v2-raffle-guilds">
              <For each={visibleGuilds()}>{(guild) => {
                const rows = createMemo(() => {
                  const list = rowData()[guild.id] || [];
                  const q = search().trim().toLowerCase();
                  if (!q) return list;
                  return list.filter(r => (r.user_tag || '').toLowerCase().includes(q));
                });

                const allRows = createMemo(() => rowData()[guild.id] || []);
                const totals = createMemo(() => {
                  const t = totalsData()[guild.id];
                  if (t && Number(t.entrants) > 0) return t;
                  return allRows().reduce((s, r) => {
                    s.entrants += 1;
                    s.entries += Number(r.entries) || 0;
                    s.tickets += Number(r.tickets) || 0;
                    s.gold += Number(r.gold) || 0;
                    return s;
                  }, { entrants: 0, entries: 0, tickets: 0, gold: 0 });
                });

                const totalTickets = () => totals().tickets || 1;
                const winners = createMemo(() => winnersData()[guild.id] || []);
                const recentWinners = createMemo(() => recentWinnersData()[guild.id] || []);
                const status = () => statusData()[guild.id] || 'open';
                const isSealed = () => !!sealedData()[guild.id];

                return (
                  <div class="v2-raffle-guild-card">
                    {/* Guild Card Header */}
                    <div class="v2-raffle-card__head">
                      <div class="v2-raffle-card__brand">
                        <span class="v2-raffle-card__emoji">{guild.emoji}</span>
                        <span class="v2-raffle-card__title">{guild.full}</span>
                      </div>

                      <div class="v2-raffle-card__badges">
                        <Show when={isSealed()}>
                          <span class="v2-raffle-card__sealed" title="Sealed in Golden Ledger">🔒 Sealed</span>
                        </Show>
                        <span class={`v2-raffle-card__status v2-raffle-card__status--${status()}`}>
                          {status() === 'drawn' ? 'Drawn' : status() === 'open' ? 'Open' : 'Pending'}
                        </span>
                      </div>
                    </div>

                    {/* Winners Showcase / Podium */}
                    <Show when={status() === 'drawn'} fallback={
                      <div class={`v2-raffle-status v2-raffle-status--${status()}`}>
                        {status() === 'open'
                          ? '🎟️ Enter to win! In-game bank deposits automatically earn tickets.'
                          : '⏳ Draw time reached · Awaiting final verification'}
                      </div>
                    }>
                      <div class="v2-raffle-podium">
                        <div class="v2-raffle-podium__hdr">🏆 Official Draw Winners</div>
                        <div class="v2-raffle-podium__list">
                          <For each={winners()}>{(w) => (
                            <div class={`v2-raffle-podium__card v2-raffle-podium__card--p${w.place}`}>
                              <div class="v2-raffle-podium__medal">{medal(Number(w.place))}</div>
                              <div class="v2-raffle-podium__body">
                                <span
                                  class="v2-raffle-podium__name v2-pick v2-pick--user"
                                  onClick={() => pickUser(w.user_tag)}
                                  title="Inspect player stats"
                                >
                                  {w.user_tag || '—'}
                                </span>
                                <div class="v2-raffle-podium__details">
                                  <Show when={w.ticket}>
                                    <span class="v2-raffle-ticket-tag" title="Exact winning ticket rolled">
                                      🎯 Ticket #{w.ticket}
                                    </span>
                                  </Show>
                                  <span class="v2-raffle-podium__prize">{gold(w.prize)}</span>
                                </div>
                              </div>
                            </div>
                          )}</For>
                        </div>
                      </div>
                    </Show>

                    {/* Summary Metric Stats */}
                    <div class="v2-stat-grid v2-raffle-metrics">
                      <div class="v2-stat v2-stat--gold">
                        <span class="v2-stat__label">Total Pot</span>
                        <span class="v2-stat__value">{gold(totals().gold)}</span>
                        <span class="v2-stat__sub">
                          {status() === 'drawn' ? 'Final prize pool' : 'Current pot'}
                        </span>
                      </div>
                      <div class={`v2-stat ${guild.accent}`}>
                        <span class="v2-stat__label">Tickets</span>
                        <span class="v2-stat__value">{qty(totals().tickets)}</span>
                        <span class="v2-stat__sub">Odds weight</span>
                      </div>
                      <div class="v2-stat">
                        <span class="v2-stat__label">Entrants</span>
                        <span class="v2-stat__value">{qty(totals().entrants)}</span>
                        <span class="v2-stat__sub">Unique players</span>
                      </div>
                      <div class="v2-stat">
                        <span class="v2-stat__label">Entries</span>
                        <span class="v2-stat__value">{qty(totals().entries)}</span>
                        <span class="v2-stat__sub">Bank deposits</span>
                      </div>
                    </div>

                    {/* Full Entrants Ledger Table */}
                    <div class="v2-raffle-ledger__head">
                      <span class="v2-raffle-ledger__title">📜 Verified Entrant Ledger</span>
                      <span class="v2-raffle-ledger__count">
                        {search()
                          ? `Showing ${rows().length} of ${allRows().length} entrants`
                          : `${allRows().length} entrants`}
                      </span>
                    </div>

                    <Show when={rows().length} fallback={<div class="v2-empty">No raffle entries match.</div>}>
                      <div class="v2-widget__scroll v2-raffle__table-scroll">
                        <table class="v2-raffle-table">
                          <thead>
                            <tr>
                              <th class="v2-raffle-th--rank" title="Entrant order by tickets deposited">#</th>
                              <th class="v2-raffle-th--name">Player (@tag)</th>
                              <th class="v2-raffle-th--range">Ticket Range</th>
                              <th class="v2-raffle-th--tickets">Tickets</th>
                              <th class="v2-raffle-th--odds">Odds</th>
                              <th class="v2-raffle-th--gold">Deposited</th>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={rows()}>{(r, i) => {
                              const tickets = Number(r.tickets) || 0;
                              const odds = totalTickets() ? ((tickets / totalTickets()) * 100).toFixed(1) : '0';
                              const win = winners().find(w => (w.user_tag || '').toLowerCase() === (r.user_tag || '').toLowerCase());
                              const isSearchMatch = search() && (r.user_tag || '').toLowerCase().includes(search().toLowerCase().trim());

                              return (
                                <tr class={`v2-raffle-row ${win ? 'v2-raffle-row--winner' : ''} ${isSearchMatch ? 'v2-raffle-row--match' : ''}`}>
                                  <td class="v2-raffle-td--rank">
                                    <span class="v2-raffle-rank-num" title={`Entrant #${r.rank || (i() + 1)} by tickets deposited`}>
                                      #{r.rank || (i() + 1)}
                                    </span>
                                  </td>
                                  <td class="v2-raffle-td--name">
                                    <div class="v2-raffle-name-wrap">
                                      <span
                                        class="v2-pick v2-pick--user v2-raffle-username"
                                        onClick={() => pickUser(r.user_tag)}
                                        title="Inspect player"
                                      >
                                        {r.user_tag || '—'}
                                      </span>
                                      <Show when={win}>
                                        <span class="v2-raffle-inline-win" title={`Official Draw ${win.place === 1 ? '1st' : win.place === 2 ? '2nd' : '3rd'} Place Winner (${gold(win.prize || 0)})`}>
                                          {medal(win.place)} {win.place === 1 ? '1st' : win.place === 2 ? '2nd' : '3rd'} Winner
                                        </span>
                                      </Show>
                                    </div>
                                  </td>
                                  <td class="v2-raffle-td--range">
                                    <span class="v2-raffle-ticket-pill">
                                      #{r.rangeStart ?? 1} – #{r.rangeEnd ?? tickets}
                                    </span>
                                  </td>
                                  <td class="v2-raffle-td--tickets">{qty(tickets)} 🎫</td>
                                  <td class="v2-raffle-td--odds">{odds}%</td>
                                  <td class="v2-raffle-td--gold">{gold(r.gold || tickets * 1000)}</td>
                                </tr>
                              );
                            }}</For>
                          </tbody>
                        </table>
                      </div>
                    </Show>

                    {/* Previous Draw History */}
                    <Show when={recentWinners().length}>
                      <div class="v2-raffle-recent">
                        <div class="v2-raffle-recent__hdr">Past Guild Winners</div>
                        <For each={recentWinners()}>{(w) => (
                          <div class="v2-raffle-recent__row">
                            <span class="v2-raffle-recent__date">{ymd(w.week_start)}</span>
                            <span class="v2-pick v2-pick--user v2-raffle-recent__user" onClick={() => pickUser(w.user_tag)}>
                              {w.user_tag}
                            </span>
                            <span class="v2-raffle-winner__prize">{gold(w.prize || 0)}</span>
                          </div>
                        )}</For>
                      </div>
                    </Show>
                  </div>
                );
              }}</For>
            </div>
          </div>
        </Show>
      </Show>
      </Show>
    </div>
  );
}